import React, { useMemo } from 'react';

// ANT Design
import { App, Button, Switch, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStore } from '../../store/store';
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { hasFormMissingValues, hasFormOptionalEmptyValues } from '../../utils/formMissingValues';
import { PANEL_LIGHT_BG, PRIMARY_DARK } from '../../constants/constants';
import logo from '../../images/symbol.svg';
import { useQueryDocumentSections } from '../../hooks';
import { hasLinkedSectionZones } from '../../utils/linkedSectionZones';
import { flushPendingSaves } from '../../collab/flushPendingSaves';
import { runReloadLinkedSections } from '../common/reloadLinkedSections';
import {
  LinkedSectionAnswersChoice,
  QUESTION_BLOCK_STYLE,
  RELOAD_TITLE_STYLE,
} from '../common/LinkedSectionAnswersChoice';

const LeftSideButton = ({ variablesGroupedByContainer }) => {
  const { modal: modalApi } = App.useApp();

  const variableOrderArray = useStore((state) => state.variableOrderArray)
  const variables = useStore(({ variables }) => variables)
  const currentProposal = useStore((state) => state.currentProposal)
  const currentDocument = useStore(({ currentDocument }) => currentDocument)
  const reloadAllApiVariables = useStore(
    (state) => state.reloadAllApiVariables
  );
  const reloadLinkedSections = useStore((state) => state.reloadLinkedSections);
  // Documents créés depuis un modèle après l'arrivée du rechargement : les
  // plus anciens n'ont pas gardé de quoi retrouver leurs sections liées.
  const hasLinkedSections = useStore(({ containers }) =>
    hasLinkedSectionZones(containers)
  );

  const { overlayProps, updateLeftShownStatus } = useOverlay();

  const isTemplate = currentDocument.type === 'template'

  const documentId = isTemplate ? currentDocument.id : currentProposal?.id;
  const { data: sectionList } = useQueryDocumentSections({
    documentType: currentDocument.type,
    documentId,
  });

  const { hasVariableErrors, hasOptionalEmpty } = useMemo(() => {
    if (!currentProposal?.id) return { hasVariableErrors: false, hasOptionalEmpty: false }
    const form = {
      variablesGroupedByContainer,
      variableOrderArray,
      variables,
      isTemplate,
      sectionList,
    }
    return {
      hasVariableErrors: hasFormMissingValues(form),
      hasOptionalEmpty: hasFormOptionalEmptyValues(form),
    }
  }, [currentProposal?.id, variables, variablesGroupedByContainer, isTemplate, variableOrderArray, sectionList])

  const shownStatus = overlayProps.left.shownStatus
  const showAllVariables = useStore(({ showAllVariables }) => showAllVariables)
  const setShowAllVariables = useStore(({ setShowAllVariables }) => setShowAllVariables)

  const handleReloadAllClick = () => {
    const documentType = currentDocument.type;
    const typeLabel = documentType === 'opportunity' ? 'opportunité' : 'offre';

    const imageElement = <img src={logo} alt="API variable" style={{ width: 20, height: 20, verticalAlign: 'sub', marginLeft: -5 }} />;

    modalApi.confirm({
      title: (
        <span>
          Recharger toutes les variables {imageElement}
        </span>
      ),
      content: (
        <div>
          <div style={{ marginBottom: 16 }}>
            Cette action va recharger toutes les variables {imageElement} en provenance de votre {typeLabel} :
          </div>
          <div style={{ marginBottom: 8 }}>
            Seront mis à jour :
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li style={{ marginBottom: 4 }}>
                Les variables textes {imageElement}
              </li>
              <li style={{ marginBottom: 4 }}>
                Les variables images {imageElement}
              </li>
              <li style={{ marginBottom: 4 }}>
                Les variables tableaux {imageElement}
              </li>
              <li style={{ marginBottom: 4 }}>
                Les variables récursives (matériel, prestations, etc.) {imageElement}
              </li>
            </ul>
          </div>
          <div style={{ marginBottom: 8 }}>
            Les réponses aux questions, les choix et sélections effectués seront conservés.
          </div>
        </div>
      ),
      okText: 'Recharger tout',
      cancelText: 'Annuler',
      onOk: () => reloadAllApiVariables && reloadAllApiVariables(),
      centered: true,
    });
  };

  const handleReloadLinkedSectionsClick = () => {
    const choices = { keepAnswers: true };
    modalApi.confirm({
      title: <span style={RELOAD_TITLE_STYLE}>Recharger les sections liées</span>,
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            Chaque section liée du document est remplacée par sa version actuelle dans la bibliothèque.
          </div>
          <div style={{ marginBottom: 16 }}>
            Tout ce qui a été modifié ou ajouté dans ces sections est perdu, y compris les blocs insérés entre
            leurs pages.
          </div>
          <div style={QUESTION_BLOCK_STYLE}>
            <LinkedSectionAnswersChoice choices={choices} />
          </div>
        </div>
      ),
      okText: 'Recharger',
      cancelText: 'Annuler',
      onOk: async () => {
        // Le contenu entier est renvoyé : ce qui attend encore part d'abord.
        if (!(await flushPendingSaves())) {
          message.error(
            "Des modifications ne sont pas encore enregistrées : réessayez dans un instant."
          );
          return;
        }
        await runReloadLinkedSections({ reloadLinkedSections, modalApi, keepAnswers: choices.keepAnswers });
      },
      centered: true,
    });
  };

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      backgroundColor: PANEL_LIGHT_BG,
      zIndex: 999,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // wrap (pas nowrap) : en proposal le panel est redimensionnable, plus
        // étroit que l'ancien mode plein écran (cf. LeftSide.jsx) — le switch
        // + le bouton recharger doivent rester atteignables plutôt que coupés
        // par l'overflowX:hidden du panel.
        flexWrap: 'wrap',
      }}>
        {/* Uniquement en template : le mode plein écran (FormCreator) n'a pas
            d'autre sortie que ce bouton. En proposal, l'icône Formulaire du
            rail fait exactement la même action (cf. LeftSideRail.jsx). */}
        {shownStatus === leftShownStatus.VARIABLES && isTemplate && (
          <Button
            type="default"
            onClick={() => updateLeftShownStatus(leftShownStatus.DOCUMENTS)}
            ghost
            icon={<FontAwesomeIcon icon={icon({ name: 'arrow-left', style: 'solid' })} />}
            style={{ whiteSpace: 'nowrap', color: PRIMARY_DARK, borderColor: PRIMARY_DARK, flexShrink: 0 }}
          >
            Retour aux documents
          </Button>
        )}
        {currentDocument.type !== 'template' && shownStatus === leftShownStatus.VARIABLES && (
          <>
            <Switch
              checkedChildren="Tout affiché"
              unCheckedChildren="Valeurs manquantes"
              checked={showAllVariables}
              onChange={setShowAllVariables}
              className="missing-values-switch"
              style={{
                width: '150px',
                flexShrink: 0,
                marginTop: 8,
                marginLeft: 4
              }}
            />
            <Button
              type="default"
              onClick={handleReloadAllClick}
              className="left-side-toolbar-btn reload-all-variables-btn"
              ghost
              icon={<FontAwesomeIcon icon={icon({ name: 'arrows-rotate', style: 'solid' })} />}
              style={{ whiteSpace: 'nowrap', color: PRIMARY_DARK, borderColor: PRIMARY_DARK }}
            >
              Recharger toutes les variables<img src={logo} alt="API variable" style={{ width: 20, height: 20, marginLeft: 4, verticalAlign: 'middle' }} />
            </Button>
            {hasLinkedSections && (
              <Button
                type="default"
                onClick={handleReloadLinkedSectionsClick}
                className="left-side-toolbar-btn reload-linked-sections-btn"
                ghost
                icon={<FontAwesomeIcon icon={icon({ name: 'link', style: 'solid' })} />}
                style={{ whiteSpace: 'nowrap', color: PRIMARY_DARK, borderColor: PRIMARY_DARK }}
              >
                Recharger les sections liées
              </Button>
            )}
          </>
        )}
      </div>
      <div style={{
        fontWeight: 'bold',
        display: shownStatus === leftShownStatus.VARIABLES ? 'none' : 'flex',
        flexDirection: 'column',
        gap: 4,
        color: PRIMARY_DARK,
        visibility: shownStatus === leftShownStatus.VARIABLES ? 'hidden' : 'visible'
      }}>
        {hasVariableErrors && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <FontAwesomeIcon
            icon={icon({ name: 'exclamation-triangle', style: 'duotone', })}
            style={{
              fontSize: 36,
              "--fa-primary-color": "black",
              "--fa-secondary-color": "yellow",
              "--fa-secondary-opacity": 1
            }}
          />
          Le formulaire possède des champs vides.
        </div>}
        {/* Même icône que dans le formulaire : repère seulement, sans effet
            sur le filigrane. */}
        {hasOptionalEmpty && <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 'normal', fontSize: 13 }}>
          <FontAwesomeIcon
            icon={icon({ name: 'circle-info', style: 'duotone' })}
            style={{
              fontSize: 20,
              width: 36,
              "--fa-primary-color": "#596579",
              "--fa-secondary-color": "#d9d9d9",
              "--fa-secondary-opacity": 1
            }}
          />
          Des valeurs optionnelles ne sont pas remplies.
        </div>}
      </div>
    </div>
  );
};

export default LeftSideButton;
